import { DatePipe, isPlatformBrowser } from "@angular/common";
import { Component, Inject, OnDestroy, OnInit, PLATFORM_ID, signal } from "@angular/core";
import { MetaDefinition } from "@angular/platform-browser";
import { ActivatedRoute, Router } from "@angular/router";
import { Revista } from "@src/app/models/interfaces/revista.interface";
import { CanonicalService } from "@src/app/servicios/canonical/canonical.service";
import { RevistaService } from "@src/app/servicios/revista/revista.service";
import { NgxExtendedPdfViewerModule, pdfDefaultOptions } from "ngx-extended-pdf-viewer";
import { Subscription, firstValueFrom } from "rxjs";
import { ConfigService } from "src/app/servicios/config/config.service";
import { GfkService } from "src/app/servicios/gfk/gfk.service";
import { PlatformService } from "src/app/servicios/platform/platform.service";
import { TitleWrapperService } from "src/app/servicios/title-wrapper/title-wrapper.service";

@Component({
  selector: "app-detalle-revista",
  templateUrl: "./detalle-revista.component.html",
  styleUrls: ["./detalle-revista.component.css"],
  imports: [NgxExtendedPdfViewerModule, DatePipe],
  providers: [RevistaService],
})
export class DetalleRevistaComponent implements OnInit, OnDestroy {
  isBrowser: boolean;
  constantesGlobales: any;
  revista = signal({});
  rSub: Subscription;

  constructor(
    private readonly revistaService: RevistaService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly router: Router,
    @Inject(PLATFORM_ID) private readonly platformId: object,
    private readonly platformService: PlatformService,
    private readonly gfkService: GfkService,
    private readonly configService: ConfigService,
    private readonly titleService: TitleWrapperService,
    private readonly canonicalService: CanonicalService,
  ) {
    pdfDefaultOptions.rangeChunkSize = 300000;
    this.isBrowser = this.platformService.isBrowser();
    this.platformService.getLocationHostname();
    this.constantesGlobales = this.configService.getConstantesGlobales();
  }

  ngOnInit(): void {
    const idAmbito = this.constantesGlobales.idAmbito;

    this.rSub = this.activatedRoute.params.subscribe(async (parms: any) => {
      if (parms.id) {
        const magazine = this.revistaService.getRevistas(idAmbito, parms.id);
        if (magazine) {
          const result: Revista[] = await firstValueFrom(magazine);
          if (result.length > 0) {
            this.revista.set(result);
          } else {
            this.router.navigateByUrl("/404");
          }
          this.updateMetaTags(result[0]);
          this.titleService.setTitle(result[0].nombre);
        }
      }
    });

    if (this.constantesGlobales.gfk === true) {
      if (isPlatformBrowser(this.platformId)) {
        this.initGfk();
      }
    }
  }

  ngOnDestroy(): void {
    if (this.rSub) this.rSub.unsubscribe();
  }

  public formatDate(obj): string {
    if (obj == null) {
      return null;
    }
    return obj.toString().replace(/\s/g, "T");
  }

  /**
   * Actualiza las metaetiquetas de la página con información relevante del post actual.
   * Genera un array de metaetiquetas que incluye datos como descripción, título, autor,
   * editor, y otros metadatos relacionados con el contenido del post.
   *
   * Las metaetiquetas generadas incluyen tanto etiquetas estándar como etiquetas específicas
   * para Open Graph (og) y Dublin Core (DC).
   *
   * @private
   */
  private updateMetaTags(revista): void {
    const url = `${this.constantesGlobales.siteDomain}/revista/${revista.id}`;
    const fecha = revista.fecha ? new Date(revista.fecha.replace(/\s/g, "T")) : null;

    const metaTagsArray: MetaDefinition[] = [
      { property: "og:title", content: revista.nombre },
      { property: "og:type", content: "article" },
      { property: "og:image", content: revista.img },
      { property: "og:url", content: url },
      { property: "og:description", content: revista.descripcion },
      { property: "article:author", content: this.constantesGlobales.siteName },
      { property: "article:publisher", content: this.constantesGlobales.siteName },
      { property: "og:site_name", content: this.constantesGlobales.siteDomain },
      { name: "DC.title", content: revista.nombre },
      { name: "DC.creator", content: this.constantesGlobales.siteTitle },
      { name: "DC.publisher", content: this.constantesGlobales.siteTitle },
      { name: "DC.description", content: revista.descripcion },
      { name: "DC.subject", content: this.constantesGlobales.metaTag },
      { name: "DC.date", content: fecha },
    ];

    this.canonicalService.updateCanonicalUrls(metaTagsArray);
  }

  private initGfk(): void {
    if (this.constantesGlobales.gfk) {
      const customParams = { c1: this.stripProtocolAndSubdomain(this.constantesGlobales.siteDomain), c2: "revistas" };
      this.gfkService.impression(customParams);
    }
  }

  private stripProtocolAndSubdomain(url: string): string {
    const strippedUrl = url.replace(/^(https?:\/\/)?(www\.)?/, "");
    return strippedUrl;
  }
}
